import isEmpty from 'lodash/isEmpty';

/*
 * LUT-524 / LUT-564: decision logic for the account-under-review wall, kept pure so the
 * predicates are unit-testable (withGrants supplies redux/API state).
 *
 * A partner-request account (organization.is_partner persisted at signup) must not see the
 * Employer Center until its request is approved. WHO decides is org-service: it classifies its
 * own status enum (exhaustively, at compile time) into `partner_review_approved`, a tri-state
 * value on the organization payload:
 *   true   - partner request approved: the wall never applies;
 *   false  - partner request explicitly not approved: the wall applies, subject to the viewer
 *            exemptions below;
 *   absent - no decision (non-partner organization, or a payload that lacks the field): the
 *            wall does not apply. Absent is reported (withGrants) so a rollback or a missed
 *            mapper is visible without harming users.
 * The frontend never interprets the raw status string. Only an explicit `false` blocks; a
 * string "false", 0, null or a missing key all fail open, because the wall is UX and real
 * access stays grant-gated everywhere.
 *
 * While the request is still not approved, upward roles for THIS organization's job-feed org
 * lift the wall for the requester - roles on a different partner organization do not.
 */

export const hasPartnerReviewDecision = organization => (
  typeof organization?.partner_review_approved === 'boolean'
);

export const isPartnerReviewDenied = organization => (
  organization?.partner_review_approved === false
);

/**
 * A partner-request organization whose payload carries no usable decision. Fails open in the
 * gate; surfaced as telemetry so it is never silent.
 */
export const isPartnerReviewDecisionMissing = organization => (
  organization?.is_partner === true && !hasPartnerReviewDecision(organization)
);

export const UPWARD_ACCESS = {
  UNKNOWN: 'unknown',
  GRANTED: 'granted',
  NONE: 'none',
  UNAVAILABLE: 'unavailable',
};

/**
 * Classify the GMS upward-grants self-read answer for the current organization.
 * GMS returns 200 with empty roles for a profile with no upward mapping, so an
 * empty answer is definitive ("not approved"), not an error.
 */
export const resolveUpwardAccess = (grantData, upwardJobFeedId) => {
  if (isEmpty(grantData?.roles)) {
    return UPWARD_ACCESS.NONE;
  }
  if (Number.isInteger(grantData?.organizationId) && grantData?.organizationId === upwardJobFeedId) {
    return UPWARD_ACCESS.GRANTED;
  }
  return UPWARD_ACCESS.NONE;
};

/**
 * Whether the review gate applies at all for this viewer + organization: a partner-request
 * organization whose review is explicitly not approved, seen by a non-approver. Approvers
 * (Jobcase employer admins and holders of the all-employer User Roles admin grants - the same
 * predicate the User Roles page uses) are exempt: they are the ones who approve requests and
 * must reach that screen.
 */
export const isPartnerAccessBlocked = ({ isAuthenticated, isApprover, organization }) => (
  isAuthenticated === true
  && !isApprover
  && organization?.is_partner === true
  && isPartnerReviewDenied(organization)
);

/**
 * Render decision: the wall shows only while access is blocked AND the grants
 * self-read gave a definitive "no upward roles for this org" answer. UNKNOWN (check
 * in flight) renders nothing so the dashboard never flashes behind the wall; UNAVAILABLE (grants call failed) fails open - the wall
 * is UX, real access stays gated by grants everywhere.
 */
export const shouldShowAccountUnderReview = (blocked, upwardAccess) => (
  blocked && upwardAccess === UPWARD_ACCESS.NONE
);

export const shouldHoldRendering = (blocked, upwardAccess) => (
  blocked && upwardAccess === UPWARD_ACCESS.UNKNOWN
);

/**
 * A grants answer is only valid for the exact viewer + organization it was fetched
 * for. Rendering happens before effects run, so a stored answer must carry its
 * lookup identity and count as UNKNOWN the moment the identity changes - otherwise
 * switching from an approved partner org to an unapproved one renders the
 * destination dashboard once before the re-fetch.
 */
export const buildReviewKey = ({ profileKey, employerId, upwardJobFeedId }) => (
  `${profileKey}:${employerId}:${upwardJobFeedId}`
);

export const effectiveUpwardAccess = (result, reviewKey) => (
  result?.reviewKey === reviewKey ? result.access : UPWARD_ACCESS.UNKNOWN
);
